import { store } from "@store";
import { openDialog } from "@store/dialogs";
import { RepeatableRowsDialog } from "./components/repeatable-rows-dialog";
import {
  repeatablePillFocus,
  repeatablePillTarget,
  type RepeatableTreeNode,
} from "./scope";

/**
 * Opens the repeatable rows pop-up. `repeatableKey` may be a nested key — the
 * dialog resolves the root group and focuses `focusKey` inside it.
 *
 * Dispatches on the store directly so non-React call sites (Froala pill
 * handlers, toolbar callbacks) can open it too.
 */
export const openRepeatableRowsDialog = (
  repeatableKey: string,
  focusKey?: string,
) => {
  store.dispatch(
    openDialog({
      id: RepeatableRowsDialog.id,
      content: RepeatableRowsDialog,
      props: { repeatableKey, focusKey },
      closeOnOutsideClick: true,
    }),
  );
};

/**
 * Opens the rows pop-up for the group a variable NAME belongs to: `name` may
 * be a bare key (`"skills"`) or a pill token (`"skills.skill"`), and may name a
 * nested group — the root that owns the data is opened with the nested group's
 * sub-card of row 1 in focus (`repeatablePillTarget`/`repeatablePillFocus`).
 *
 * Returns false and does nothing when `name`'s first segment is not a known
 * repeatable key, so callers can fall through to their ordinary handling
 * (plain variable popover, integration tokens like `hubspot.contact.email`).
 *
 * Single home for the routing every repeatable entry point needs — the badge
 * on a marked entity and the pill click handlers in Froala, Text.editor and
 * EditableContent — so they cannot disagree about which dialog a nested key
 * opens.
 */
export const openRepeatableRowsForName = (
  name: string,
  roots: RepeatableTreeNode[],
): boolean => {
  const target = repeatablePillTarget(name, roots);
  if (!target) return false;
  openRepeatableRowsDialog(target.rootKey, repeatablePillFocus(target));
  return true;
};
